import React from "react";

import { animated, useSpring, config } from "@react-spring/web";
import { Group } from "@visx/group";
import { scaleLinear } from "@visx/scale";
import { Arc } from "@visx/shape";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";

import { Box, Stack, useTheme } from "@mui/material";

import ValueText from "./ValueText";

interface DonutChartProps {
  width: number;
  height: number;
  /** value between 0 and 100% **/
  value: number;
  /** label to show in center of donut defaults to value **/
  label?: string;
  /** a secondary label to show under the label in the in donut **/
  subLabel?: string;
  /** if set, show a marker at the given value **/
  markerValue?: number | null;
  /** arc fill color **/
  fill?: string;
  loading?: boolean;
  empty?: boolean;
  showSubLabel?: boolean;
  showValue?: boolean;
  tooltip: React.ReactNode;
}

const TEXT_SPACING = 18;
const MARGIN = 4;
const PIE_WIDTH = 15;
const TWO_PI = Math.PI * 2;

const DonutChart = ({
  loading,
  empty,
  width,
  height,
  value,
  markerValue,
  label,
  subLabel,
  fill,
  showValue,
  showSubLabel,
  tooltip,
}: DonutChartProps) => {
  const theme = useTheme();
  const innerWidth = width - MARGIN * 2;
  const innerHeight = height - MARGIN * 2;
  const radius = Math.min(innerWidth, innerHeight) / 2;
  const midRadius = radius - PIE_WIDTH / 2;
  const centerY = height / 2;
  const centerX = width / 2;

  const { tooltipOpen, tooltipLeft, tooltipTop, hideTooltip, showTooltip } =
    useTooltip<{ text: string }>();

  const { containerRef, containerBounds, TooltipInPortal } = useTooltipInPortal(
    {
      detectBounds: true,
    }
  );

  const scaleValue = scaleLinear({
    domain: [0, 100],
    range: [0, TWO_PI],
  });

  const { endAngle, startAngle } = useSpring({
    from: { endAngle: 0, startAngle: 0 },
    to: { endAngle: Math.max(0, scaleValue(value ?? 0) - 0.25), startAngle: 0 },
    config: config.slow,
  });

  const { endAngle2, startAngle2 } = useSpring({
    from: { endAngle2: 0.25, startAngle2: 0 },
    to: { endAngle2: scaleValue(value ?? 0), startAngle2: 0 },
    config: config.slow,
  });

  const { loadingOpacity } = useSpring({
    from: { loadingOpacity: 0.5 },
    to: { loadingOpacity: 1 },
    loop: { reverse: true },
    config: config.default,
  });

  const AnimatedArc = animated(Arc);

  const textTop = showSubLabel ? centerY : centerY + 5; // centered when numMeetings is displayed

  const handleTooltip = (event: React.TouchEvent | React.MouseEvent) => {
    const left =
      ("clientX" in event ? event.clientX : 0) - containerBounds.left;
    const top =
      ("clientY" in event ? event.clientY : 0) - containerBounds.top + 10;

    showTooltip({
      tooltipTop: top,
      tooltipLeft: left,
    });
  };

  return (
    <Box
      onTouchStart={handleTooltip}
      onTouchMove={handleTooltip}
      onMouseMove={handleTooltip}
      onMouseLeave={hideTooltip}
      sx={{ cursor: "pointer" }}
    >
      <svg
        width={Math.max(0, width)}
        height={Math.max(0, height)}
        ref={containerRef}
      >
        {empty ? null : (
          <>
            {showValue ? (
              <ValueText x={centerX} y={textTop} fontSize={21}>
                {label ?? `${value}`}
              </ValueText>
            ) : null}
            {showSubLabel ? (
              <ValueText
                x={centerX}
                y={textTop + TEXT_SPACING}
                fontSize={14}
                fill="#656565"
              >
                {subLabel}
              </ValueText>
            ) : null}
          </>
        )}
        <Group top={centerY} left={centerX}>
          {/* Base arc */}
          <AnimatedArc
            startAngle={0}
            endAngle={TWO_PI} //in radians
            outerRadius={radius}
            innerRadius={radius - PIE_WIDTH}
            padAngle={0}
            cornerRadius={3}
            fill={theme.palette.shades.ghost[300]}
            opacity={loading ? loadingOpacity : 1}
          />
          {empty || loading ? null : (
            <>
              {/* Fill arc */}
              <AnimatedArc
                startAngle={startAngle}
                endAngle={endAngle} // in radians
                outerRadius={radius}
                innerRadius={radius - PIE_WIDTH}
                cornerRadius={0}
                fill={fill}
                opacity={1}
              />
              {/* Rounded edge of fill arc */}
              <AnimatedArc
                startAngle={startAngle2}
                endAngle={endAngle2} // in radians
                outerRadius={radius}
                innerRadius={radius - PIE_WIDTH}
                cornerRadius={4}
                fill={fill}
                opacity={1}
              />
            </>
          )}
          {markerValue && (
            <circle
              r={PIE_WIDTH / 2}
              fill="#fff"
              stroke="#4C47CC"
              strokeWidth={4}
              cx={midRadius * Math.sin(scaleValue(markerValue))}
              cy={midRadius * Math.cos(scaleValue(markerValue) + Math.PI)}
            />
          )}
        </Group>
      </svg>
      {tooltipOpen && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          unstyled
          applyPositionStyle
        >
          <Stack
            sx={{
              backgroundColor: "#000",
              color: "#fafafa",
              fontWeight: 500,
              borderRadius: "4px",
              padding: "6px 13px",
            }}
          >
            {tooltip}
          </Stack>
        </TooltipInPortal>
      )}
    </Box>
  );
};
export default DonutChart;
